import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cachedFetchJson } from "../utils/apiCache";

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [active, setActive] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isQuoteExpanded, setIsQuoteExpanded] = useState(false);

  const handleNext = () => {
    if (testimonials.length === 0) return;
    setActive((prev) => (prev + 1) % testimonials.length);
  };

  const handlePrev = () => {
    if (testimonials.length === 0) return;
    setActive((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const randomRotateY = () => Math.floor(Math.random() * 21) - 10;

  useEffect(() => {
    if (testimonials.length < 2) return;
    const interval = setInterval(() => {
      setActive((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  useEffect(() => {
    if (testimonials.length === 0) return;
    if (active >= testimonials.length) setActive(0);
  }, [active, testimonials.length]);

  useEffect(() => {
    let cancelled = false;
    let requestUrl = "";

    const loadTestimonials = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const backendRoot = process.env.REACT_APP_BACKEND;
        const apiBase = process.env.REACT_APP_API_BASE_URL;
        const baseUrl = (backendUrl || apiBase || backendRoot || "http://localhost:5000").replace(
          /\/$/,
          "",
        );
        const defaultPath = backendUrl ? "/testimonials" : "/api/testimonials";
        const path = process.env.REACT_APP_TESTIMONIALS_PATH || defaultPath;
        const params = new URLSearchParams({ status: "active", limit: "6" });
        requestUrl = `${baseUrl}${path}?${params.toString()}`;
        const data = await cachedFetchJson(requestUrl, { ttlMs: 120000 });
        const assetBase = (
          process.env.REACT_APP_ASSET_BASE_URL ||
          backendRoot ||
          "http://localhost:8000"
        ).replace(/\/$/, "");
        const imageBase = `${assetBase}/uploads`;
        const dataArray = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        const normalized = dataArray
          ? dataArray
              .map((item) => ({
                quote: item.message || "",
                name: item.name || "Anonymous",
                designation: item.designation || "",
                rating:
                  typeof item.rating === "number"
                    ? item.rating
                    : Number(item.rating) || 0,
                src: item.image
                  ? item.image.startsWith("http")
                    ? item.image
                    : `${imageBase}/${item.image.replace(/^\//, "")}`
                  : "",
              }))
              .filter((item) => item.quote && item.name)
          : [];

        if (cancelled) return;
        if (normalized.length > 0) {
          setTestimonials(normalized);
        } else {
          setTestimonials([]);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching testimonials:", err, requestUrl);
          setError("Unable to load testimonials right now. Please try again.");
          setTestimonials([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadTestimonials();

    return () => {
      cancelled = true;
    };
  }, []);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <i
        key={i}
        className={`fa-star ${
          i < rating ? "fa-solid text-[#ffcc66]" : "fa-regular text-white/30"
        }`}
      ></i>
    ));
  };

  const hasTestimonials = testimonials.length > 0;
  const activeTestimonial = hasTestimonials ? testimonials[active] : null;
  const quoteText = activeTestimonial?.quote || "";
  const isLongQuote = quoteText.trim().length > 140;

  useEffect(() => {
    setIsQuoteExpanded(false);
  }, [active]);

  return (
    <div className="mx-auto max-w-sm px-4 py-15 font-sans antialiased md:max-w-6xl md:px-8 lg:px-12">
      {/* TOP HEADING */}
      <div className="lg:mb-16 mb-5 text-center">
        <h2 className="text-3xl font-extrabold tracking-tight text-[#351F72] md:text-4xl">
          What Our Clients Say
        </h2>
        <p className="mx-auto max-w-xl text-gray-500">
          Real experiences from our happy customers who trusted our services.
        </p>
      </div>

      <div className="relative grid grid-cols-1 gap-16 md:grid-cols-2">
        {/* IMAGE SLIDER */}
        <div>
          <div className="relative h-64 w-full sm:h-72 md:h-80 lg:h-96">
            <AnimatePresence>
              {hasTestimonials &&
                testimonials.map((testimonial, index) => {
                  const isActive = index === active;

                  return (
                    <motion.div
                      key={`${testimonial.src}-${testimonial.name}-${index}`}
                      initial={{
                        opacity: 0,
                        scale: 0.9,
                        z: -100,
                        rotate: randomRotateY(),
                      }}
                      animate={{
                        opacity: isActive ? 1 : 0.7,
                        scale: isActive ? 1 : 0.95,
                        z: isActive ? 0 : -100,
                        rotate: isActive ? 0 : randomRotateY(),
                        zIndex: isActive
                          ? 40
                          : testimonials.length + 2 - index,
                        y: isActive ? [0, -80, 0] : 0,
                      }}
                      exit={{
                        opacity: 0,
                        scale: 0.9,
                        z: 100,
                        rotate: randomRotateY(),
                      }}
                      transition={{
                        duration: 0.4,
                        ease: "easeInOut",
                      }}
                      className="absolute inset-0 origin-bottom"
                    >
                      {testimonial.src ? (
                        <img
                          src={testimonial.src}
                          alt={testimonial.name}
                          draggable={false}
                          className="h-full w-full rounded-3xl object-cover object-center shadow-lg"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center rounded-3xl bg-gray-100 text-gray-500">
                          {testimonial.name}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
            </AnimatePresence>
            {!hasTestimonials && (
              <div className="flex h-full items-center justify-center rounded-3xl bg-gray-100 text-gray-500">
                {isLoading ? "Loading testimonials..." : "No testimonials yet"}
              </div>
            )}
          </div>
        </div>

        {/* TEXT SECTION */}
        <div className="flex flex-col justify-between py-2">
          {error && (
            <p className="mb-4 text-sm text-red-500" role="alert">
              {error}
            </p>
          )}
          {hasTestimonials && (
            <motion.div
              key={active}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
            >
              {/* RATING */}
              <div className="mb-3 flex items-center gap-2">
                <div className="flex gap-1">{renderStars(activeTestimonial?.rating || 0)}</div>
                <span className="inline-flex items-center gap-1 rounded-full bg-[#f1f5ff] px-2.5 py-1 text-xs font-semibold">
                
                  <span aria-label="Google brand text">
                    <span className="text-[#4285F4]">G</span>
                    <span className="text-[#DB4437]">o</span>
                    <span className="text-[#F4B400]">o</span>
                    <span className="text-[#4285F4]">g</span>
                    <span className="text-[#0F9D58]">l</span>
                    <span className="text-[#DB4437]">e</span>
                  </span>
                  <span className="text-[#000]">Reviews</span>
                </span>
              </div>

              <h3 className="text-2xl font-bold text-black">
                {activeTestimonial?.name}
              </h3>

              <p className="text-sm text-gray-500">
                {activeTestimonial?.designation}
              </p>

              {/* Quote: mobile shows partial + read more; desktop shows full animated */}
              <div className="mt-6">
                <p
                  className={`text-base text-gray-500 sm:hidden ${
                    isQuoteExpanded ? "" : "line-clamp-4"
                  }`}
                >
                  {quoteText}
                </p>

                {isLongQuote && (
                  <button
                    type="button"
                    className="sm:hidden mt-2 text-xs font-semibold text-[#1F3C88] underline underline-offset-4"
                    onClick={() => setIsQuoteExpanded((prev) => !prev)}
                  >
                    {isQuoteExpanded ? "Show less" : "Read more"}
                  </button>
                )}

                <motion.p className="mt-8 hidden text-lg text-gray-500 sm:block">
                  {quoteText.split(" ").map((word, index) => (
                    <motion.span
                      key={index}
                      initial={{ filter: "blur(10px)", opacity: 0, y: 5 }}
                      animate={{ filter: "blur(0px)", opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.2,
                        ease: "easeInOut",
                        delay: 0.02 * index,
                      }}
                      className="inline-block"
                    >
                      {word}&nbsp;
                    </motion.span>
                  ))}
                </motion.p>
              </div>
            </motion.div>
          )}

          {/* CONTROLS */}
          <div className="flex gap-4 pt-12 md:pt-0">
            <button
              onClick={handlePrev}
              disabled={!hasTestimonials}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 transition hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <i className="fa-solid fa-arrow-left text-black"></i>
            </button>

            <button
              onClick={handleNext}
              disabled={!hasTestimonials}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 transition hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <i className="fa-solid fa-arrow-right text-black"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
